import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import XLSXStyle from 'xlsx-js-style';
import {
  Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography
} from '@mui/material';
import {
  UploadFile as UploadIcon, Download as DownloadIcon, CheckCircle as OkIcon,
  ErrorOutline as BadIcon, PersonAddAlt1 as NewIcon, DeleteOutline as ClearIcon
} from '@mui/icons-material';
import { IMPORT_ORGANIZATION_MEMBERS } from 'api/graphql/organization';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Columnas del import = campos del perfil, para que el miembro quede lo más completo posible.
// `department` = ÁREA de la empresa (Bienestar Corporativo); "departamento" sigue siendo
// el geográfico (state) para no romper plantillas que ya circulan.
const COLUMNS = ['first_name', 'last_name', 'email', 'birthdate', 'gender', 'identification_number', 'phone', 'city', 'state', 'country', 'department', 'eps', 'contact_name', 'contact_phone'];

const HEADER_MAP = {
  first_name: ['first_name', 'firstname', 'nombre', 'nombres', 'name'],
  last_name: ['last_name', 'lastname', 'apellido', 'apellidos'],
  email: ['email', 'correo', 'e-mail', 'mail'],
  birthdate: ['birthdate', 'fecha_nacimiento', 'nacimiento', 'fecha de nacimiento', 'fecha_de_nacimiento'],
  gender: ['gender', 'genero', 'género', 'sexo'],
  identification_number: ['identification_number', 'identificacion', 'identificación', 'documento', 'cedula', 'cédula', 'id', 'nid'],
  phone: ['phone', 'telefono', 'teléfono', 'celular', 'movil', 'móvil'],
  city: ['city', 'ciudad', 'municipio'],
  state: ['state', 'departamento', 'depto', 'provincia', 'estado'],
  country: ['country', 'pais', 'país'],
  department: ['department', 'area', 'área', 'sede', 'equipo', 'dependencia', 'departamento_empresa', 'departamento de empresa'],
  eps: ['eps', 'salud', 'aseguradora'],
  contact_name: ['contact_name', 'contacto', 'nombre_contacto', 'nombre de contacto'],
  contact_phone: ['contact_phone', 'telefono_contacto', 'teléfono de contacto', 'telefono de contacto']
};

const normKey = (k) => String(k).trim().toLowerCase();

const mapRow = (raw) => {
  const out = {};
  COLUMNS.forEach((c) => { out[c] = ''; });
  Object.keys(raw).forEach((key) => {
    const nk = normKey(key);
    Object.entries(HEADER_MAP).forEach(([field, aliases]) => {
      if (aliases.includes(nk)) out[field] = raw[key];
    });
  });
  return out;
};

const toGender = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (['m', 'male', 'masculino', 'hombre'].includes(v)) return 'MALE';
  if (['f', 'female', 'femenino', 'mujer'].includes(v)) return 'FEMALE';
  if (['o', 'other', 'otro'].includes(v)) return 'OTHER';
  return null;
};

// Devuelve YYYY-MM-DD si es una fecha de calendario válida; null si no (rechaza 1996-58-56, etc.).
const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  const m = String(value).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  if (dt.getFullYear() !== Number(y) || dt.getMonth() !== Number(mo) - 1 || dt.getDate() !== Number(d)) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

function OrganizationImport({ organizationId, onImported = null, value, onChange }) {
  const { t } = useTranslation(['organizations', 'buttons']);
  const inputRef = useRef(null);
  const { rows, fileName, report } = value;
  const [importMembers, { loading: importing }] = useMutation(IMPORT_ORGANIZATION_MEMBERS);

  const setValue = (patch) => onChange({ ...value, ...patch });

  const validateRow = (m, seen) => {
    const errors = [];
    if (!String(m.first_name).trim()) errors.push(t('fName', { defaultValue: 'Nombre' }));
    if (!String(m.last_name).trim()) errors.push(t('fLastName', { defaultValue: 'Apellido' }));
    const email = String(m.email || '').trim().toLowerCase();
    if (!EMAIL_RE.test(email)) errors.push(t('fEmail', { defaultValue: 'Correo' }));
    else if (seen.has(email)) errors.push(t('fDuplicate', { defaultValue: 'Correo duplicado' }));
    if (!parseDate(m.birthdate)) errors.push(t('fBirthdate', { defaultValue: 'Fecha' }));
    if (!String(m.city).trim()) errors.push(t('fCity', { defaultValue: 'Ciudad' }));
    if (!String(m.state).trim()) errors.push(t('fState', { defaultValue: 'Departamento' }));
    if (!String(m.country).trim()) errors.push(t('fCountry', { defaultValue: 'País' }));
    return { email, errors };
  };

  const parseFile = async (file) => {
    const buffer = await file.arrayBuffer();
    // codepage 65001 = UTF-8: garantiza que las tildes se lean bien en CSV.
    const wb = XLSX.read(buffer, { type: 'array', cellDates: true, codepage: 65001 });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

    const seen = new Set();
    const parsed = json.map((raw, i) => {
      const m = mapRow(raw);
      const { email, errors } = validateRow(m, seen);
      if (email && EMAIL_RE.test(email)) seen.add(email);
      return {
        row: i + 1,
        first_name: String(m.first_name).trim(),
        last_name: String(m.last_name).trim(),
        email,
        birthdate: parseDate(m.birthdate),
        gender: toGender(m.gender),
        city: String(m.city).trim(),
        state: String(m.state).trim(),
        country: String(m.country).trim(),
        department: String(m.department).trim(),
        identification_number: String(m.identification_number).trim(),
        phone: String(m.phone).trim(),
        eps: String(m.eps).trim(),
        contact_name: String(m.contact_name).trim(),
        contact_phone: String(m.contact_phone).trim(),
        errors
      };
    });
    setValue({ rows: parsed, fileName: file.name, report: null });
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = '';
  };

  const validRows = rows.filter((r) => r.errors.length === 0);
  const invalidRows = rows.filter((r) => r.errors.length > 0);

  const handleImport = async () => {
    if (validRows.length === 0) return;
    const members = validRows.map((r) => ({
      first_name: r.first_name,
      last_name: r.last_name,
      email: r.email,
      birthdate: r.birthdate,
      gender: r.gender,
      city: r.city,
      state: r.state,
      country: r.country,
      department: r.department || null,
      identification_number: r.identification_number || null,
      phone: r.phone || null,
      eps: r.eps || null,
      contact_name: r.contact_name || null,
      contact_phone: r.contact_phone || null
    }));
    const res = await importMembers({ variables: { organization_id: organizationId, members } });
    const backend = res?.data?.importOrganizationMembers;
    const clientSkipped = invalidRows.map((r) => ({ row: r.row, email: r.email || null, reason: r.errors.join(', ') }));
    setValue({
      rows: [],
      fileName: '',
      report: {
        created: backend?.created ?? 0,
        linked: backend?.linked_existing ?? 0,
        createdMembers: backend?.created_members ?? [],
        skipped: [...(backend?.skipped ?? []), ...clientSkipped]
      }
    });
    if (onImported) onImported();
  };

  const clearAll = () => setValue({ rows: [], fileName: '', report: null });

  // Plantilla Excel estilizada: encabezados en celeste, negrilla y bordes negros.
  const downloadTemplate = () => {
    const example = ['Juan', 'Pérez', 'juan.perez@empresa.com', '1990-05-14', 'MALE', '1020304050', '3001234567', 'Cartagena', 'Bolívar', 'Colombia', 'Tecnología', 'Nueva EPS', 'María Pérez', '3009876543'];
    const ws = XLSXStyle.utils.aoa_to_sheet([COLUMNS, example]);
    const border = { style: 'thin', color: { rgb: '111111' } };
    const allBorders = { top: border, bottom: border, left: border, right: border };
    const headerStyle = {
      font: { bold: true, color: { rgb: '10233A' } },
      fill: { fgColor: { rgb: 'BDE0FE' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: allBorders
    };
    COLUMNS.forEach((_, c) => {
      const ref = XLSXStyle.utils.encode_cell({ r: 0, c });
      if (ws[ref]) ws[ref].s = headerStyle;
      const ref2 = XLSXStyle.utils.encode_cell({ r: 1, c });
      if (ws[ref2]) ws[ref2].s = { border: allBorders };
    });
    ws['!cols'] = COLUMNS.map((h) => ({ wch: Math.max(14, h.length + 3) }));
    ws['!rows'] = [{ hpt: 22 }];
    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, 'miembros');
    XLSXStyle.writeFile(wb, 'plantilla_miembros.xlsx');
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('importHelp', { defaultValue: 'Sube un archivo CSV o Excel con los miembros. Los nuevos reciben una invitación por correo para definir su contraseña; los que ya existen solo se vinculan a esta organización.' })}
      </Typography>

      {/* Zona de carga */}
      <Box
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        sx={{
          p: 3, mb: 2, borderRadius: 3, cursor: 'pointer', textAlign: 'center',
          border: '2px dashed', borderColor: 'hairline.strong', backgroundColor: 'surface.overlay',
          transition: 'all 150ms ease', '&:hover': { borderColor: 'gold.main', backgroundColor: 'gold.soft' }
        }}
      >
        <UploadIcon sx={{ fontSize: 34, color: 'var(--otx-gold-text)' }} />
        <Typography variant="body1" sx={{ fontWeight: 700, mt: 1 }}>
          {fileName || t('chooseFile', { defaultValue: 'Elegir archivo CSV o Excel' })}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('importFormats', { defaultValue: 'Columnas: nombre, apellido, correo, fecha, género, identificación, teléfono, ciudad, departamento, país…' })}
        </Typography>
      </Box>
      <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={onPick} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {(rows.length > 0 || fileName) ? (
          <Button variant="text" startIcon={<ClearIcon />} onClick={clearAll} sx={{ textTransform: 'none', fontWeight: 700, color: (th) => th.palette.deleteAction.fg }}>
            {t('clearFile', { defaultValue: 'Eliminar archivo' })}
          </Button>
        ) : <Box />}
        <Button variant="text" startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={{ textTransform: 'none', fontWeight: 700, color: 'var(--otx-gold-text)' }}>
          {t('downloadTemplate', { defaultValue: 'Descargar plantilla' })}
        </Button>
      </Stack>

      {/* Reporte del import */}
      {report && (
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.overlay' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip icon={<NewIcon />} label={`${t('created', { defaultValue: 'Creados' })}: ${report.created}`} sx={{ backgroundColor: 'rgba(27,147,75,0.16)', color: '#5FE0A0', fontWeight: 700 }} />
            <Chip label={`${t('linked', { defaultValue: 'Vinculados' })}: ${report.linked}`} sx={{ backgroundColor: 'surface.raised', color: 'text.secondary', fontWeight: 700 }} />
            <Chip label={`${t('omitted', { defaultValue: 'Omitidos' })}: ${report.skipped.length}`} sx={{ backgroundColor: report.skipped.length ? 'rgba(178,22,22,0.16)' : 'surface.raised', color: report.skipped.length ? '#ffb3b3' : 'text.secondary', fontWeight: 700 }} />
          </Stack>

          {report.createdMembers.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('newMembers', { defaultValue: 'Nuevos registrados' })}</Typography>
              <Stack sx={{ mt: 0.5 }} spacing={0.25}>
                {report.createdMembers.map((m) => (
                  <Typography key={m.email} variant="body2">{m.name} — {m.email}</Typography>
                ))}
              </Stack>
            </Box>
          )}

          {report.skipped.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('omittedRows', { defaultValue: 'Filas omitidas' })}</Typography>
              <Stack sx={{ mt: 0.5 }} spacing={0.25}>
                {report.skipped.map((s) => (
                  <Typography key={`${s.row}-${s.email}`} variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('rowLabel', { defaultValue: 'Fila' })} {s.row}: {s.email || '—'} — {s.reason}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Preview con validación por campo */}
      {rows.length > 0 && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
            <Chip icon={<OkIcon />} label={`${validRows.length} ${t('valid', { defaultValue: 'válidos' })}`} sx={{ backgroundColor: 'rgba(27,147,75,0.16)', color: '#5FE0A0', fontWeight: 700 }} />
            {invalidRows.length > 0 && (
              <Chip icon={<BadIcon />} label={`${invalidRows.length} ${t('withError', { defaultValue: 'con error' })}`} sx={{ backgroundColor: 'rgba(178,22,22,0.16)', color: '#ffb3b3', fontWeight: 700 }} />
            )}
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" color="primary" onClick={handleImport} disabled={importing || validRows.length === 0} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {importing ? <CircularProgress size={18} color="inherit" /> : `${t('import', { defaultValue: 'Importar' })} ${validRows.length}`}
            </Button>
          </Stack>

          <Box sx={{ maxHeight: 380, overflow: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'hairline.main' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('name', { defaultValue: 'Nombre' })}</TableCell>
                  <TableCell>{t('email', { defaultValue: 'Correo' })}</TableCell>
                  <TableCell>{t('location', { defaultValue: 'Ubicación' })}</TableCell>
                  <TableCell align="center">{t('status', { defaultValue: 'Estado' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.row}>
                    <TableCell>{r.row}</TableCell>
                    <TableCell>{`${r.first_name} ${r.last_name}`.trim() || '—'}</TableCell>
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{[r.city, r.state, r.country].filter(Boolean).join(', ') || '—'}</TableCell>
                    <TableCell align="center">
                      {r.errors.length
                        ? <Chip size="small" label={r.errors.join(', ')} sx={{ backgroundColor: 'rgba(178,22,22,0.16)', color: '#ffb3b3', fontWeight: 600 }} />
                        : <OkIcon sx={{ color: '#5FE0A0', fontSize: 18 }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}

OrganizationImport.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onImported: PropTypes.func,
  value: PropTypes.shape({
    rows: PropTypes.array,
    fileName: PropTypes.string,
    report: PropTypes.object
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

export default OrganizationImport;
